import { useState } from "react";
import { Check, Loader2, Pencil } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuthStore } from "@/stores/auth.store";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { updateMe } from "@/api/auth";
import { initials } from "@/lib/formatters";
import { resolveAvatarColor } from "@/lib/avatarColor";
import { TAG_COLOR_KEYS, TAG_COLORS } from "@/lib/constants";
import { cn } from "@/lib/utils";

export function ProfileSection() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const { roleName, isSuperadmin } = useCurrentProject();

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(user?.full_name ?? "");
  const [savingName, setSavingName] = useState(false);
  const [savingColor, setSavingColor] = useState<string | null>(null);
  const [colorOpen, setColorOpen] = useState(false);

  if (!user) return null;

  const avatarColor = resolveAvatarColor(user.avatar_color, user.id);
  const memberSince = new Date(user.created_at).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  async function saveName() {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === user!.full_name) {
      setEditingName(false);
      setNameDraft(user!.full_name);
      return;
    }
    setSavingName(true);
    try {
      const updated = await updateMe({ full_name: trimmed });
      setUser(updated);
      toast.success("Name updated");
      setEditingName(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSavingName(false);
    }
  }

  async function pickColor(color: string) {
    if (color === user!.avatar_color) {
      setColorOpen(false);
      return;
    }
    setSavingColor(color);
    try {
      const updated = await updateMe({ avatar_color: color });
      setUser(updated);
      setColorOpen(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSavingColor(null);
    }
  }

  return (
    <Section title="Profile">
      <div className="flex items-start gap-6">
        <Popover open={colorOpen} onOpenChange={setColorOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="group relative shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300/50"
              aria-label="Change avatar color"
            >
              <Avatar className="h-20 w-20">
                <AvatarFallback
                  className={cn(
                    "text-2xl font-semibold",
                    avatarColor.bg,
                    avatarColor.text,
                  )}
                >
                  {initials(user.full_name)}
                </AvatarFallback>
              </Avatar>
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/30 text-white opacity-0 transition-opacity group-hover:opacity-100">
                <Pencil className="h-5 w-5" />
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[268px] p-3">
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Avatar color
            </p>
            <div className="grid grid-cols-6 gap-2">
              {TAG_COLOR_KEYS.map((c) => {
                const tag = TAG_COLORS[c];
                const active = user!.avatar_color === c;
                const isSaving = savingColor === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => void pickColor(c)}
                    disabled={savingColor !== null}
                    aria-label={c}
                    aria-pressed={active}
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-full transition-transform",
                      tag.dot,
                      active
                        ? "ring-2 ring-offset-2 ring-slate-900"
                        : "hover:scale-110",
                      savingColor && !isSaving && "opacity-60",
                    )}
                  >
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin text-white" />
                    ) : active ? (
                      <Check className="h-4 w-4 text-white" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>

        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Full name
            </label>
            {editingName ? (
              <div className="mt-1 flex items-center gap-2">
                <Input
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  disabled={savingName}
                  onBlur={() => void saveName()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void saveName();
                    if (e.key === "Escape") {
                      setNameDraft(user.full_name);
                      setEditingName(false);
                    }
                  }}
                  className="h-9 rounded-lg"
                />
                {savingName && (
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setNameDraft(user.full_name);
                  setEditingName(true);
                }}
                className="mt-1 flex items-center gap-2 text-base font-semibold text-slate-900 hover:text-indigo-600"
              >
                {user.full_name}
                <Pencil className="h-3.5 w-3.5 text-slate-400" />
              </button>
            )}
          </div>

          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Email
            </label>
            <p className="mt-1 text-sm text-slate-700">{user.email}</p>
            <p className="mt-1 text-xs text-slate-400">
              Contact your administrator to change your email.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Role
              </p>
              <span className="mt-1 inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                {isSuperadmin ? "Superadmin" : (roleName ?? "—")}
              </span>
            </div>
            <div className="ml-auto">
              <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Member since
              </p>
              <p className="mt-1 text-sm text-slate-600">{memberSince}</p>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-plane bg-white p-6 shadow-plane">
      <h2 className="mb-5 text-lg font-medium tracking-tight text-slate-900">
        {title}
      </h2>
      {children}
    </section>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Something went wrong.";
}
